import { useEffect, useState } from "react";
import "./HealthPage.css";

const RPC_URLS = ["https://api.moneyxpro.com/rpc", "https://rpc.beschyperchain.com"];
const API_URL = import.meta.env.VITE_COPY_TRADING_API || "https://api.moneyxpro.com";
// Liquidity consolidated into SynthVault (single pool).
const VAULT_ADDR = "0xdB15dF10C2203291D9Fa218b487f924C3B017d6E";
const SYNTH_VAULT_ADDR = "0xdB15dF10C2203291D9Fa218b487f924C3B017d6E";
const RPC_TIMEOUT = 10_000;

type Status = "checking" | "ok" | "degraded" | "down";

interface ServiceStatus {
  name: string;
  description: string;
  status: Status;
  latency?: number;
  detail?: string;
}

const INITIAL: ServiceStatus[] = [
  { name: "BESC RPC Node",        description: "Primary blockchain RPC endpoint",       status: "checking" },
  { name: "Vault Contract",       description: "MLP vault — getMinPrice(WBESC)",        status: "checking" },
  { name: "SynthVault Contract",  description: "Synthetic trading vault",               status: "checking" },
  { name: "Trading API",          description: "Backend — positions & copy trading",    status: "checking" },
  { name: "Stats API",            description: "Analytics & volume data",               status: "checking" },
];

async function rpcRequest(method: string, params: any[] = []) {
  let lastError: Error | undefined;

  for (const url of RPC_URLS) {
    const t0 = Date.now();

    try {
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ jsonrpc: "2.0", id: 1, method, params }),
        signal: AbortSignal.timeout(RPC_TIMEOUT),
      });
      const json = await res.json();

      if (!res.ok || json.error) {
        throw new Error(json.error?.message || `HTTP ${res.status}`);
      }

      return { result: json.result, latency: Date.now() - t0, url };
    } catch (e: any) {
      lastError = e;
    }
  }

  throw lastError ?? new Error("RPC unavailable");
}

async function checkRpc(): Promise<Partial<ServiceStatus>> {
  try {
    const { result, latency } = await rpcRequest("eth_blockNumber");
    const block = parseInt(result, 16);
    return { status: block > 0 ? "ok" : "degraded", latency, detail: `Block #${block.toLocaleString()}` };
  } catch (e: any) {
    return { status: "down", latency: RPC_TIMEOUT, detail: e.message };
  }
}

async function checkContract(addr: string): Promise<Partial<ServiceStatus>> {
  try {
    const { result, latency } = await rpcRequest("eth_getCode", [addr, "latest"]);
    const code: string = result ?? "0x";
    return {
      status: code && code.length > 4 ? "ok" : "down",
      latency,
      detail: code.length > 4 ? `Contract deployed (${Math.round(code.length / 2)} bytes)` : "No bytecode",
    };
  } catch (e: any) {
    return { status: "down", latency: RPC_TIMEOUT, detail: e.message };
  }
}

async function checkHttp(url: string, path: string): Promise<Partial<ServiceStatus>> {
  const t0 = Date.now();
  try {
    const res = await fetch(`${url}${path}`, { signal: AbortSignal.timeout(6000) });
    const latency = Date.now() - t0;
    if (res.ok) return { status: "ok", latency, detail: `HTTP ${res.status}` };
    return { status: "degraded", latency, detail: `HTTP ${res.status}` };
  } catch (e: any) {
    return { status: "down", latency: Date.now() - t0, detail: e.message };
  }
}

export default function HealthPage() {
  const [services, setServices] = useState<ServiceStatus[]>(INITIAL);
  const [checkedAt, setCheckedAt] = useState<Date | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const runChecks = async () => {
    setRefreshing(true);
    setServices(INITIAL);

    const [rpc, vault, synthVault, api, stats] = await Promise.all([
      checkRpc(),
      checkContract(VAULT_ADDR),
      checkContract(SYNTH_VAULT_ADDR),
      checkHttp(API_URL, "/health"),
      checkHttp("https://stats.moneyxpro.com", "/"),
    ]);

    setServices([
      { ...INITIAL[0], ...rpc },
      { ...INITIAL[1], ...vault },
      { ...INITIAL[2], ...synthVault },
      { ...INITIAL[3], ...api },
      { ...INITIAL[4], ...stats },
    ]);
    setCheckedAt(new Date());
    setRefreshing(false);
  };

  useEffect(() => {
    runChecks();
    const id = setInterval(runChecks, 60_000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const overall: Status =
    services.some((s) => s.status === "checking")
      ? "checking"
      : services.some((s) => s.status === "down")
      ? "down"
      : services.some((s) => s.status === "degraded")
      ? "degraded"
      : "ok";

  const overallLabel: Record<Status, string> = {
    checking:  "Checking systems…",
    ok:        "All Systems Operational",
    degraded:  "Partial Degradation",
    down:      "Major Outage Detected",
  };

  return (
    <div className="HP-root">
      <div className="HP-bg-glow" aria-hidden="true" />

      <div className="HP-card">
        {/* Header */}
        <div className="HP-logo-row">
          <div className="HP-logo-icon">M</div>
          <span className="HP-logo-text">MONEY<span>X</span></span>
        </div>

        <div className={`HP-overall HP-overall--${overall}`}>
          <div className={`HP-overall-dot HP-dot--${overall}`} />
          <span>{overallLabel[overall]}</span>
        </div>

        {/* Services */}
        <div className="HP-services">
          {services.map((svc) => (
            <div key={svc.name} className="HP-service-row">
              <div className="HP-service-left">
                <div className={`HP-dot HP-dot--${svc.status}`} />
                <div>
                  <div className="HP-service-name">{svc.name}</div>
                  <div className="HP-service-desc">{svc.description}</div>
                </div>
              </div>
              <div className="HP-service-right">
                {svc.status === "checking" ? (
                  <div className="HP-spinner" />
                ) : (
                  <>
                    <span className={`HP-badge HP-badge--${svc.status}`}>
                      {svc.status === "ok" ? "Operational" : svc.status === "degraded" ? "Degraded" : "Down"}
                    </span>
                    {svc.latency !== undefined && (
                      <span className="HP-latency">{svc.latency}ms</span>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Detail list */}
        <div className="HP-detail-list">
          {services.filter((s) => s.detail).map((svc) => (
            <div key={svc.name} className="HP-detail-row">
              <span className="HP-detail-name">{svc.name}</span>
              <span className="HP-detail-val">{svc.detail}</span>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="HP-footer">
          <span className="HP-checked-at">
            {checkedAt
              ? `Last checked: ${checkedAt.toLocaleTimeString()}`
              : "Checking…"}
          </span>
          <button
            className={`HP-refresh-btn${refreshing ? " HP-refresh-btn--spinning" : ""}`}
            onClick={runChecks}
            disabled={refreshing}
          >
            ↻ Refresh
          </button>
        </div>

        <div className="HP-note">
          Auto-refreshes every 60 seconds · Funds are always safe on-chain
        </div>
      </div>
    </div>
  );
}
